import { useEffect, useMemo, useRef, useState } from 'react'
import {
    Headphones, ImagePlus, Loader2, LogOut, Mic, MicOff, MonitorPlay,
    MonitorUp, MonitorX, PhoneOff, Settings2, Video, VideoOff, Volume2, VolumeX
} from 'lucide-react'
import type { ShareKind } from '@shared/protocol'
import { MriButton, MriPopover, MriPopoverContent, MriPopoverTrigger, cn } from '@mriqbox/ui-kit'
import {
    QUALITY_PRESETS, RESOLUTION_ORDER, RESOLUTION_PRESETS, cameraUnavailableMessage,
    listCameras, screenShareUnavailableMessage, type ShareQuality, type ShareResolution
} from '@/lib/screenCapture'
import { listMicrophones, microphoneSupported } from '@/lib/voiceCapture'
import { ENCODER_CHOICES, ENCODER_ORDER, type EncoderChoice } from '@/lib/videoEncoder'
import { RECEIVE_ORDER, RECEIVE_PRESETS, type ReceivePreference } from '@/lib/receiveQuality'
import { UploadLimitMenu } from '@/components/UploadLimitMenu'
import type { UploadLimit } from '@/lib/uploadLimit'
import { BACKGROUND_PRESETS, readBackgroundFile } from '@/lib/backgrounds'
import type { BackgroundEffect } from '@/lib/videoEffects'

export interface ControlDockProps {
    /** Tipos no ar agora. Tela e camera podem estar as duas. */
    sharingKinds: Set<ShareKind>
    canShareScreen: boolean
    canUseCamera: boolean
    quality: ShareQuality
    /** Teto de resolucao que quem transmite escolheu. */
    resolution: ShareResolution
    /** Placa de video ou processador codificando o que sai daqui. */
    encoder: EncoderChoice
    /** O que esta codificando de verdade, quando ha o que dizer. */
    encoderNote?: string | null
    cameraId: string | null
    background: BackgroundEffect
    /** Baixando o runtime do efeito (11 MB na primeira vez). */
    effectLoading: boolean
    /** Liga o tipo pedido, ou desliga quando ele ja esta no ar. */
    onToggleShare: (kind: ShareKind) => void
    /** Troca a tela ou janela transmitida sem interromper a sessao. */
    onChangeScreen?: () => void
    /** A tela no ar esta mandando som? Sem som, nao ha o que cortar. */
    screenHasAudio?: boolean
    /** O som da tela que estou mandando esta cortado. */
    screenAudioMuted?: boolean
    onToggleScreenAudio?: () => void
    onQualityChange: (quality: ShareQuality) => void
    onResolutionChange: (resolution: ShareResolution) => void
    onEncoderChange: (encoder: EncoderChoice) => void
    receivePreference?: ReceivePreference
    onReceivePreferenceChange?: (preference: ReceivePreference) => void
    uploadLimit?: UploadLimit
    onUploadLimitChange?: (limit: UploadLimit) => void
    onCameraChange: (deviceId: string) => void
    onBackgroundChange: (effect: BackgroundEffect) => void
    // ── voz ───────────────────────────────────────────────────────────────
    inVoice: boolean
    muted: boolean
    deafened: boolean
    voiceLoading: boolean
    micId: string | null
    noiseSuppression: boolean
    onToggleVoice: () => void
    onToggleMute: () => void
    onToggleDeafen: () => void
    onMicChange: (deviceId: string) => void
    onNoiseSuppressionChange: (enabled: boolean) => void
    onLeave: () => void
    /**
     * A barra deve estar a mostra?
     *
     * Quem decide e o palco: com transmissao no ar ela some sozinha e volta
     * quando o mouse se mexe (veja `useDockAutoHide`). Aqui dentro isso e so
     * uma sugestao — a barra se recusa a sumir enquanto alguem esta mexendo
     * nela, senao ela fugiria do cursor no meio do clique.
     */
    visible?: boolean
}

/**
 * Todo botao da barra tem a mesma forma: circulo, so o icone dentro.
 *
 * O nome de cada um vive no `aria-label` e no `title` — sem texto na tela, e
 * de la que sai tanto a dica do mouse quanto o que o leitor de tela anuncia.
 */
const BOTAO = 'h-10 w-10 shrink-0 rounded-full'

/** Barra flutuante do rodape — as acoes que a pessoa usa durante a sala. */
export function ControlDock({
    sharingKinds,
    canShareScreen,
    canUseCamera,
    quality,
    resolution,
    encoder,
    encoderNote = null,
    cameraId,
    background,
    effectLoading,
    onToggleShare,
    onChangeScreen,
    screenHasAudio = false,
    screenAudioMuted = false,
    onToggleScreenAudio,
    onQualityChange,
    onResolutionChange,
    onEncoderChange,
    receivePreference = 'auto',
    onReceivePreferenceChange,
    uploadLimit = 'none',
    onUploadLimitChange,
    onCameraChange,
    onBackgroundChange,
    inVoice,
    muted,
    deafened,
    voiceLoading,
    micId,
    noiseSuppression,
    onToggleVoice,
    onToggleMute,
    onToggleDeafen,
    onMicChange,
    onNoiseSuppressionChange,
    onLeave,
    visible = true
}: ControlDockProps) {
    const sharingScreen = sharingKinds.has('screen')
    const sharingCamera = sharingKinds.has('camera')
    const cameras = useCameras(sharingCamera)
    const fileRef = useRef<HTMLInputElement>(null)
    const [uploadError, setUploadError] = useState<string | null>(null)
    const microphones = useMicrophones(inVoice)
    const canUseVoice = useMemo(() => microphoneSupported(), [])
    /** Mouse em cima da barra, ou foco de teclado dentro dela. */
    const [emUso, setEmUso] = useState(false)
    /**
     * Menu de ajustes aberto. Precisa ser controlado: o conteudo do popover
     * sai por portal, fora da barra, entao levar o mouse ate ele dispara o
     * `pointerleave` daqui — e a barra sumiria por baixo do proprio menu.
     */
    const [ajustesAbertos, setAjustesAbertos] = useState(false)
    const [telaBloqueadaAberta, setTelaBloqueadaAberta] = useState(false)
    const aMostra = visible || emUso || ajustesAbertos

    /*
     * O que estes botoes SAO, dito em texto.
     *
     * Nenhum deles mostra texto na tela, e um botao so com desenho nao tem
     * nome nenhum: leitor de tela anuncia "botao" e o mouse nao mostra dica. O
     * rotulo muda com o estado porque o botao tambem muda — o mesmo lugar
     * comeca e para a transmissao.
     */
    const rotuloTela = sharingScreen ? 'Parar de compartilhar a tela' : 'Compartilhar tela'
    const rotuloCamera = sharingCamera ? 'Desligar a camera' : 'Ligar a camera'

    return (
        <div
            className={cn(
                'pointer-events-none absolute inset-x-0 bottom-4 flex justify-center px-2 transition-all duration-200',
                aMostra ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
            )}
        >
            {/*
              * Rola na horizontal em vez de estourar a tela: numa sala em que
              * tudo esta ligado sao nove controles, e nenhum celular cabe todos.
              * Quebrar em duas linhas comeria o palco; a rolagem so aparece
              * quando falta espaco.
              *
              * Escondida ela para de receber clique — senao seria um campo
              * minado de botoes invisiveis no rodape do palco. O foco de
              * teclado continua chegando de proposito: quem navega por Tab faz
              * a barra reaparecer em vez de esbarrar num nada.
              */}
            <div
                onPointerEnter={() => setEmUso(true)}
                onPointerLeave={() => setEmUso(false)}
                onFocusCapture={() => setEmUso(true)}
                onBlurCapture={() => setEmUso(false)}
                className={cn(
                    'flex max-w-full items-center gap-1.5 overflow-x-auto overscroll-x-contain rounded-full border border-border/70 bg-card/95 p-1.5 shadow-2xl shadow-black/50',
                    aMostra ? 'pointer-events-auto' : 'pointer-events-none'
                )}
            >
                {canShareScreen ? (
                    <MriButton
                        variant={sharingScreen ? 'destructive' : 'secondary'}
                        size="icon"
                        className={BOTAO}
                        onClick={() => onToggleShare('screen')}
                        aria-label={rotuloTela}
                        aria-pressed={sharingScreen}
                        title={rotuloTela}
                    >
                        {sharingScreen
                            ? <MonitorX className="h-4 w-4" />
                            : <MonitorPlay className="h-4 w-4" />}
                    </MriButton>
                ) : (
                    /*
                     * Bloqueado, o botao explica em vez de so apagar.
                     *
                     * O motivo morava no `title`, que num celular nunca
                     * aparece: nao ha o que passar o mouse por cima. Quem abria
                     * a sala pelo iPhone via um botao cinza sem nenhuma pista de
                     * por que ele nao responde — e captura de tela nao existe em
                     * navegador nenhum do iOS, entao e ali que a pergunta sempre
                     * aparece. Botao `disabled` nao recebe clique, por isso ele
                     * segue clicavel com `aria-disabled`.
                     */
                    <MriPopover open={telaBloqueadaAberta} onOpenChange={setTelaBloqueadaAberta}>
                        <MriPopoverTrigger asChild>
                            <MriButton
                                variant="secondary"
                                size="icon"
                                className={cn(BOTAO, 'opacity-50')}
                                aria-disabled
                                aria-label={rotuloTela}
                                title={screenShareUnavailableMessage() ?? undefined}
                            >
                                <MonitorPlay className="h-4 w-4" />
                            </MriButton>
                        </MriPopoverTrigger>
                        <MriPopoverContent side="top" className="w-64 text-xs leading-relaxed">
                            {screenShareUnavailableMessage()}
                        </MriPopoverContent>
                    </MriPopover>
                )}

                {/*
                  * Cortar o som que EU mando, sem parar a transmissao. Existe
                  * tambem no painel lateral; aqui porque e nesta barra que a
                  * pessoa mexe enquanto transmite, e ela cobre o palco em tela
                  * cheia, onde a barra lateral nem aparece.
                  */}
                {sharingScreen && screenHasAudio && (
                    <MriButton
                        variant={screenAudioMuted ? 'destructive' : 'secondary'}
                        size="icon"
                        className={BOTAO}
                        onClick={onToggleScreenAudio}
                        aria-pressed={screenAudioMuted}
                        title={screenAudioMuted
                            ? 'Voltar a mandar o som da tela'
                            : 'Parar de mandar o som da tela (a imagem continua)'}
                    >
                        {screenAudioMuted
                            ? <VolumeX className="h-4 w-4" />
                            : <Volume2 className="h-4 w-4" />}
                    </MriButton>
                )}

                {sharingScreen && (
                    <MriButton
                        variant="secondary"
                        size="icon"
                        className={BOTAO}
                        onClick={onChangeScreen}
                        disabled={!canShareScreen}
                        aria-label="Editar transmissão"
                        title="Trocar a janela ou tela compartilhada sem interromper a transmissão"
                    >
                        <MonitorUp className="h-4 w-4" />
                    </MriButton>
                )}

                {/* Uma publicacao por pessoa: ligar a camera troca a tela por ela. */}
                <MriButton
                    variant={sharingCamera ? 'destructive' : 'secondary'}
                    size="icon"
                    className={BOTAO}
                    onClick={() => onToggleShare('camera')}
                    disabled={!canUseCamera}
                    aria-label={rotuloCamera}
                    aria-pressed={sharingCamera}
                    title={canUseCamera ? rotuloCamera : (cameraUnavailableMessage() ?? undefined)}
                >
                    {sharingCamera
                        ? <VideoOff className="h-4 w-4" />
                        : <Video className="h-4 w-4" />}
                </MriButton>

                <span className="mx-0.5 h-6 w-px bg-border" aria-hidden />

                {/* Microfone so e pedido ao entrar na voz: ninguem entra na
                    sala com o microfone aberto sem perceber. */}
                <MriButton
                    variant={inVoice ? (muted ? 'destructive' : 'secondary') : 'ghost'}
                    size="icon"
                    className={BOTAO}
                    onClick={inVoice ? onToggleMute : onToggleVoice}
                    disabled={!canUseVoice || voiceLoading}
                    title={!canUseVoice
                        ? 'Seu navegador ou dispositivo nao da acesso ao microfone'
                        : inVoice
                            ? (muted ? 'Ativar o microfone' : 'Silenciar o microfone')
                            : 'Entrar na voz'}
                    aria-label={inVoice ? (muted ? 'Ativar o microfone' : 'Silenciar o microfone') : 'Entrar na voz'}
                    aria-pressed={inVoice && !muted}
                >
                    {voiceLoading
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : inVoice && !muted
                            ? <Mic className="h-4 w-4" />
                            : <MicOff className="h-4 w-4" />}
                </MriButton>

                {inVoice && (
                    <>
                        <MriButton
                            variant={deafened ? 'destructive' : 'ghost'}
                            size="icon"
                            className={BOTAO}
                            onClick={onToggleDeafen}
                            title={deafened ? 'Voltar a ouvir a sala' : 'Silenciar a sala'}
                            aria-label={deafened ? 'Voltar a ouvir a sala' : 'Silenciar a sala'}
                            aria-pressed={deafened}
                        >
                            {deafened ? <VolumeX className="h-4 w-4" /> : <Headphones className="h-4 w-4" />}
                        </MriButton>

                        {/* Icone, e nao a palavra "sair": era o unico botao
                            da barra escrito por extenso, e a acao ja tem um
                            desenho que todo mundo conhece de telefone. */}
                        <MriButton
                            variant="ghost"
                            size="icon"
                            className={BOTAO}
                            onClick={onToggleVoice}
                            title="Sair da voz"
                            aria-label="Sair da voz"
                        >
                            <PhoneOff className="h-4 w-4 text-destructive" />
                        </MriButton>
                    </>
                )}

                <MriPopover open={ajustesAbertos} onOpenChange={setAjustesAbertos}>
                    <MriPopoverTrigger asChild>
                        <MriButton variant="ghost" size="icon" className={BOTAO} title="Qualidade da transmissao" aria-label="Qualidade da transmissao">
                            <Settings2 className="h-4 w-4" />
                        </MriButton>
                    </MriPopoverTrigger>
                    {/* Teto de altura com rolagem propria: o menu foi crescendo
                        (qualidade, fundo, microfone, cameras) ate passar da
                        altura da tela e ter o topo cortado. */}
                    <MriPopoverContent
                        align="center"
                        side="top"
                        className="max-h-[min(70vh,34rem)] w-64 overflow-y-auto overscroll-contain p-2"
                    >
                        <p className="sticky top-0 z-10 bg-popover px-2 pb-2 pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                            Qualidade
                        </p>
                        {(Object.keys(QUALITY_PRESETS) as ShareQuality[]).map((key) => {
                            const preset = QUALITY_PRESETS[key]
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => onQualityChange(key)}
                                    className={cn(
                                        'w-full rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent',
                                        quality === key && 'bg-accent'
                                    )}
                                >
                                    <span className="block text-sm font-medium text-foreground">{preset.label}</span>
                                    <span className="block text-[11px] text-muted-foreground">
                                        {key === 'nitido' && sharingCamera
                                            ? '15 fps — pensado pra texto; a camera fica travada'
                                            : preset.description}
                                    </span>
                                </button>
                            )
                        })}

                        {/*
                          * Resolucao fica junto da qualidade porque as duas
                          * respondem a mesma pergunta ("quanto isto vai custar
                          * pra mim e pra quem assiste?") por eixos diferentes:
                          * a qualidade escolhe a cadencia, esta escolhe o
                          * tamanho. Quem transmite codigo em fonte pequena sobe
                          * pra nativa; quem esta num notebook fraco ou numa
                          * rede ruim desce, e ganha imagem estavel no lugar de
                          * pixels que o encoder nem consegue entregar.
                          */}
                        <p className="sticky top-0 z-10 mt-2 border-t border-border/60 bg-popover px-2 pb-2 pt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                            Resolucao
                        </p>
                        {RESOLUTION_ORDER.map((key) => {
                            const preset = RESOLUTION_PRESETS[key]
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => onResolutionChange(key)}
                                    aria-pressed={resolution === key}
                                    className={cn(
                                        'w-full rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent',
                                        resolution === key && 'bg-accent'
                                    )}
                                >
                                    <span className="block text-sm font-medium text-foreground">{preset.label}</span>
                                    <span className="block text-[11px] text-muted-foreground">
                                        {preset.description}
                                    </span>
                                </button>
                            )
                        })}

                        {/*
                          * Qualidade e resolucao dizem QUANTO codificar; esta
                          * diz ONDE. E a saida de quem ve a maquina inteira
                          * travar ao transmitir sem ter que piorar a imagem —
                          * e a volta, pra quem tem placa que estraga o video.
                          */}
                        <p className="sticky top-0 z-10 mt-2 border-t border-border/60 bg-popover px-2 pb-2 pt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                            Codificacao
                        </p>
                        {ENCODER_ORDER.map((key) => {
                            const preset = ENCODER_CHOICES[key]
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => onEncoderChange(key)}
                                    aria-pressed={encoder === key}
                                    className={cn(
                                        'w-full rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent',
                                        encoder === key && 'bg-accent'
                                    )}
                                >
                                    <span className="block text-sm font-medium text-foreground">{preset.label}</span>
                                    <span className="block text-[11px] text-muted-foreground">
                                        {preset.description}
                                    </span>
                                </button>
                            )
                        })}
                        {encoderNote && (
                            <p className="px-2 pb-1 pt-1 text-[11px] text-muted-foreground" role="status">
                                {encoderNote}
                            </p>
                        )}

                        {onUploadLimitChange && (
                            <UploadLimitMenu
                                value={uploadLimit}
                                onChange={onUploadLimitChange}
                                headingClassName="sticky top-0 z-10 mt-2 border-t border-border/60 bg-popover px-2 pb-2 pt-3 text-[10px] uppercase tracking-widest text-muted-foreground"
                            />
                        )}

                        {onReceivePreferenceChange && (
                        <p className="sticky top-0 z-10 mt-2 border-t border-border/60 bg-popover px-2 pb-2 pt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                            O que você recebe
                        </p>
                        )}
                        {onReceivePreferenceChange && RECEIVE_ORDER.map((key) => {
                            const preset = RECEIVE_PRESETS[key]
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => onReceivePreferenceChange?.(key)}
                                    aria-pressed={receivePreference === key}
                                    className={cn(
                                        'w-full rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent',
                                        receivePreference === key && 'bg-accent'
                                    )}
                                >
                                    <span className="block text-sm font-medium text-foreground">{preset.label}</span>
                                    <span className="block text-[11px] text-muted-foreground">
                                        {preset.description}
                                    </span>
                                </button>
                            )
                        })}

                        {canUseCamera && (
                            <>
                                <p className="sticky top-0 z-10 mt-2 flex items-center gap-2 border-t border-border/60 bg-popover px-2 pb-2 pt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Fundo da camera
                                    {effectLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                                </p>

                                <div className="grid grid-cols-3 gap-1.5 px-2 pb-1">
                                    {BACKGROUND_PRESETS.map((preset) => (
                                        <button
                                            key={preset.id}
                                            type="button"
                                            onClick={() => {
                                                setUploadError(null)
                                                onBackgroundChange(preset.effect)
                                            }}
                                            disabled={effectLoading}
                                            aria-pressed={sameEffect(background, preset.effect)}
                                            title={preset.label}
                                            className={cn(
                                                'flex flex-col items-center gap-1 rounded-lg p-1 transition-colors hover:bg-accent',
                                                sameEffect(background, preset.effect) && 'bg-accent',
                                                effectLoading && 'opacity-60'
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'h-9 w-full rounded-md border',
                                                    sameEffect(background, preset.effect)
                                                        ? 'border-primary'
                                                        : 'border-border/60'
                                                )}
                                                style={{ background: preset.swatch }}
                                            />
                                            <span className="w-full truncate text-center text-[10px] text-muted-foreground">
                                                {preset.label}
                                            </span>
                                        </button>
                                    ))}

                                    {/* A imagem vira data URL aqui mesmo: nao sobe pro servidor
                                        nem vai pros outros — eles recebem o video ja composto. */}
                                    <button
                                        type="button"
                                        onClick={() => fileRef.current?.click()}
                                        disabled={effectLoading}
                                        title="Usar uma imagem do computador"
                                        className={cn(
                                            'flex flex-col items-center gap-1 rounded-lg p-1 transition-colors hover:bg-accent',
                                            effectLoading && 'opacity-60'
                                        )}
                                    >
                                        <span className="flex h-9 w-full items-center justify-center rounded-md border border-dashed border-border/70">
                                            <ImagePlus className="h-4 w-4 text-muted-foreground" />
                                        </span>
                                        <span className="w-full truncate text-center text-[10px] text-muted-foreground">
                                            Enviar
                                        </span>
                                    </button>
                                </div>

                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={async (event) => {
                                        const file = event.target.files?.[0]
                                        // Zera o valor pra que escolher o MESMO arquivo de novo dispare o evento.
                                        event.target.value = ''
                                        if (!file) return
                                        try {
                                            setUploadError(null)
                                            onBackgroundChange(await readBackgroundFile(file))
                                        } catch (error) {
                                            setUploadError(error instanceof Error ? error.message : 'Imagem invalida.')
                                        }
                                    }}
                                />

                                <p className="px-2 pb-1 text-[11px] text-muted-foreground">
                                    {uploadError ?? (effectLoading
                                        ? 'Preparando o efeito...'
                                        : 'Vale so pra camera. Pode pesar em maquina mais fraca.')}
                                </p>
                            </>
                        )}

                        {canUseVoice && (
                            <>
                                <p className="sticky top-0 z-10 mt-2 border-t border-border/60 bg-popover px-2 pb-2 pt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Microfone
                                </p>
                                <button
                                    type="button"
                                    onClick={() => onNoiseSuppressionChange(!noiseSuppression)}
                                    aria-pressed={noiseSuppression}
                                    className={cn(
                                        'w-full rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent',
                                        noiseSuppression && 'bg-accent'
                                    )}
                                >
                                    <span className="block text-sm font-medium text-foreground">Reducao de ruido</span>
                                    <span className="block text-[11px] text-muted-foreground">
                                        Corta teclado, ventilador e barulho de fundo
                                    </span>
                                </button>

                                {microphones.length > 1 && microphones.map((device, index) => (
                                    <button
                                        key={device.deviceId}
                                        type="button"
                                        onClick={() => onMicChange(device.deviceId)}
                                        className={cn(
                                            'w-full truncate rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-accent',
                                            micId === device.deviceId && 'bg-accent'
                                        )}
                                    >
                                        {device.label || `Microfone ${index + 1}`}
                                    </button>
                                ))}
                            </>
                        )}

                        {/* So aparece com mais de uma camera: com uma so, escolher nao faz sentido. */}
                        {cameras.length > 1 && (
                            <>
                                <p className="sticky top-0 z-10 mt-2 border-t border-border/60 bg-popover px-2 pb-2 pt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Camera
                                </p>
                                {cameras.map((device, index) => (
                                    <button
                                        key={device.deviceId}
                                        type="button"
                                        onClick={() => onCameraChange(device.deviceId)}
                                        className={cn(
                                            'w-full truncate rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-accent',
                                            cameraId === device.deviceId && 'bg-accent'
                                        )}
                                    >
                                        {/* O rotulo so existe depois da primeira permissao concedida. */}
                                        {device.label || `Camera ${index + 1}`}
                                    </button>
                                ))}
                            </>
                        )}
                    </MriPopoverContent>
                </MriPopover>

                <span className="mx-0.5 h-6 w-px bg-border" aria-hidden />

                <MriButton
                    variant="destructive"
                    size="icon"
                    className={BOTAO}
                    onClick={onLeave}
                    title="Sair da sala"
                    aria-label="Sair da sala"
                >
                    <LogOut className="h-4 w-4" />
                </MriButton>
            </div>
        </div>
    )
}

/** Microfones disponiveis, relidos quando a voz entra no ar (rotulos). */
function useMicrophones(active: boolean): MediaDeviceInfo[] {
    const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([])

    useEffect(() => {
        let cancelled = false
        const refresh = () => {
            void listMicrophones().then((devices) => {
                if (!cancelled) setMicrophones(devices)
            })
        }
        refresh()

        const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
        media?.addEventListener?.('devicechange', refresh)
        return () => {
            cancelled = true
            media?.removeEventListener?.('devicechange', refresh)
        }
    }, [active])

    return microphones
}

/** Mesma opcao selecionada? Imagem compara pela origem do arquivo. */
function sameEffect(a: BackgroundEffect, b: BackgroundEffect): boolean {
    if (a.kind !== b.kind) return false
    return a.kind === 'image' && b.kind === 'image' ? a.src === b.src : true
}

/**
 * Lista de cameras, relida quando a camera entra no ar: e nesse momento que o
 * navegador libera os rotulos dos dispositivos (antes da permissao eles vem
 * vazios) e que faz diferenca poder trocar de uma pra outra.
 */
function useCameras(active: boolean): MediaDeviceInfo[] {
    const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])

    useEffect(() => {
        let cancelled = false
        const refresh = () => {
            void listCameras().then((devices) => {
                if (!cancelled) setCameras(devices)
            })
        }
        refresh()

        // Webcam plugada ou removida no meio da sala.
        const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
        media?.addEventListener?.('devicechange', refresh)
        return () => {
            cancelled = true
            media?.removeEventListener?.('devicechange', refresh)
        }
    }, [active])

    return cameras
}
